import { RunContextApp } from 'framework/RCApp';
import type { MediaAssetVersion } from 'features/contentManagement/mediaContent/mediaAssetWorkflow';
import type {
  GetMediaImagesParams,
  GetMediaImagesResponse,
  GetMediaImagesResult,
  GetUnresolvedConfigParams,
  GetUnresolvedConfigResult,
  MappedMediaImageFields,
  MediaConfigImageItem,
  RevertMediaConfigParams,
  RevertMediaConfigResponse,
  RevertMediaConfigResult,
  PromoteMediaConfigParams,
  PromoteMediaConfigResponse,
  PromoteMediaConfigResult,
  GetMediaEnvMatrixParams,
  GetMediaEnvMatrixResult,
  MediaEnvMatrixConfigEntry,
  MediaEnvMatrixEnvironment,
  MediaEnvMatrixResponse,
  UploadMediaParams,
  UploadMediaResult,
} from 'types/apis/mediaInterfaces';
import type { AssetType, JourneyTab } from 'features/contentManagement/mediaContent/mediaAssetWorkflow';
import {
  projectEnvironmentToAppEnv,
  type AppEnvironment,
} from 'features/contentManagement/mediaContent/mediaEnvMapping';

const JOURNEY_BUCKETS: { key: 'email' | 'dashboard' | 'her'; tab: JourneyTab }[] = [
  { key: 'email', tab: 'emails' },
  { key: 'dashboard', tab: 'web' },
  { key: 'her', tab: 'her' },
];

const isMediaConfigImageItem = (value: unknown): value is MediaConfigImageItem =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/** Flatten journey buckets that may be an array or a category/section-keyed map. */
const flattenMediaBucket = (
  bucket: unknown,
  options?: { categoryKey?: string },
): MediaConfigImageItem[] => {
  if (Array.isArray(bucket)) {
    return bucket.filter(isMediaConfigImageItem);
  }

  if (!bucket || typeof bucket !== 'object') return [];

  const items: MediaConfigImageItem[] = [];
  for (const [groupKey, groupItems] of Object.entries(bucket as Record<string, unknown>)) {
    if (!Array.isArray(groupItems)) continue;

    groupItems.forEach((entry) => {
      if (!isMediaConfigImageItem(entry)) return;
      items.push({
        ...entry,
        category: entry.category ?? options?.categoryKey ?? groupKey,
      });
    });
  }

  return items;
};

const flattenBrandGuidelines = (bucket: unknown): MediaConfigImageItem[] =>
  flattenMediaBucket(bucket);

/** Coerce API values that may be string, number, or nested objects. */
const asString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(asString).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['name', 'code', 'id', 'section', 'value', 'label', 'displayName']) {
      const nested = asString(record[key]);
      if (nested) return nested;
    }
  }
  return '';
};

const trimString = (value: unknown): string => asString(value).trim();

/** Section paths from API — string or string[] (e.g. ["EMAIL.ALL.HEADER", "EMAIL.ALL.HEADER_V3"]). */
const toSectionPathList = (value: unknown): string[] => {
  if (value == null) return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === 'string') return entry.trim();
        return trimString(entry);
      })
      .filter(Boolean);
  }
  const coerced = trimString(value);
  return coerced ? [coerced] : [];
};

/** Last segment after splitting on "." — e.g. EMAIL.ALL.HEADER → HEADER */
const getSectionNameFromPath = (path: string): string => {
  const segment = path.split('.').filter(Boolean).pop();
  return segment ?? path;
};

const resolveSectionPaths = (value: unknown): string[] => [...new Set(toSectionPathList(value))];

const resolveSectionNames = (value: unknown): string[] => {
  const names = resolveSectionPaths(value).map(getSectionNameFromPath).filter(Boolean);
  return [...new Set(names)];
};

/** Section label for UI grouping — last path segment, uppercased (e.g. FEEDBACK, FOOTER). */
const toSectionGroupLabel = (sectionName: string): string => sectionName.toUpperCase();

const resolveSectionGroups = (item: MediaConfigImageItem): string[] => {
  const fromSection = resolveSectionNames(item.section).map(toSectionGroupLabel);
  if (fromSection.length > 0) return fromSection;

  const component = trimString(item.component);
  if (component) return [humanizeLabel(component).toUpperCase()];

  return ['GENERAL'];
};

/** Brand Guidelines bucket groups by API category, not section. */
const resolveBrandCategoryGroups = (item: MediaConfigImageItem): string[] => {
  const category = trimString(item.category);
  if (category) return [category];

  const component = trimString(item.component);
  if (component) return [humanizeLabel(component)];

  return ['GENERAL'];
};

const resolveAssetGroups = (item: MediaConfigImageItem, journeyTab: JourneyTab): string[] =>
  journeyTab === 'brand' ? resolveBrandCategoryGroups(item) : resolveSectionGroups(item);

const normalizeImageUrl = (value: unknown): string => {
  const raw = trimString(value);
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('//')) return `https:${raw}`;
  return '';
};

const resolveImageUrl = (item: MediaConfigImageItem): string => {
  const candidates = [item.configValue, item.config_value, item.defaultValue, item.default_value];
  for (const candidate of candidates) {
    const url = normalizeImageUrl(candidate);
    if (url) return url;
  }
  return '';
};

const humanizeLabel = (value: string): string =>
  value
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const resolveCategory = (item: MediaConfigImageItem, journeyTab: JourneyTab): string =>
  resolveAssetGroups(item, journeyTab)[0] ?? 'GENERAL';

const resolveAssetType = (item: MediaConfigImageItem): AssetType => {
  const haystack = [
    item.configKey,
    item.component,
    item.configDataType,
    item.configClassification,
  ]
    .map(asString)
    .join(' ')
    .toLowerCase();
  if (haystack.includes('video')) return 'Video';
  if (haystack.includes('icon')) return 'Icon';
  return 'Image';
};

const resolveRegistryMapping = (item: MediaConfigImageItem): string => {
  const key = trimString(item.configKey);
  const type = trimString(item.configType);
  if (type && key) return `${type}.${key}`;
  return key || type || '';
};

const IMAGE_DIMENSION_LOOKUP_TYPE = 'IMAGE_DIMENSION';

/** e.g. 18_X_23_PX → 18 x 23 px */
export const formatImageDimensionLookupCode = (code: string): string => {
  const trimmed = code.trim();
  if (!trimmed) return '';

  return trimmed
    .replace(/_X_/gi, ' x ')
    .replace(/_PX$/i, ' px')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const resolveImageDimensionLookup = (
  item: MediaConfigImageItem,
): { dimensions: string; dimensionLookupCode?: string } => {
  const lookups = item._lookups;
  if (!Array.isArray(lookups) || lookups.length === 0) return { dimensions: '' };

  const dimensionLookup = lookups.find(
    (lookup) => trimString(lookup.lookupType).toUpperCase() === IMAGE_DIMENSION_LOOKUP_TYPE,
  );
  const code = trimString(dimensionLookup?.code);
  if (!code) return { dimensions: '' };

  return {
    dimensions: formatImageDimensionLookupCode(code),
    dimensionLookupCode: code,
  };
};

export const mapMediaConfigImageItem = (
  item: MediaConfigImageItem,
  journeyTab: JourneyTab,
  index: number,
): MappedMediaImageFields => {
  const assetId = trimString(item.configKey) || `media-${index}`;
  const fileURL = resolveImageUrl(item);
  const { dimensions, dimensionLookupCode } = resolveImageDimensionLookup(item);

  return {
    assetId,
    name: trimString(item.displayName) || humanizeLabel(assetId),
    type: resolveAssetType(item),
    journeyTab,
    category: resolveCategory(item, journeyTab),
    fileURL,
    thumbnailURL: fileURL,
    dimensions,
    dimensionLookupCode,
    registryMapping: resolveRegistryMapping(item),
    sectionMapping: resolveSectionPaths(item.section).join(', ') || trimString(item.component),
    productLine: trimString(item.configType),
    description: trimString(item.configDocumentation),
  };
};

export const transformMediaImagesResponseToAssets = (
  response: GetMediaImagesResponse,
  pilotId?: string,
): MediaAssetVersion[] => {
  const assets: MediaAssetVersion[] = [];

  const appendAssets = (items: MediaConfigImageItem[], tab: JourneyTab) => {
    items.forEach((item, index) => {
      const mapped = mapMediaConfigImageItem(item, tab, index);
      const assetGroups = resolveAssetGroups(item, tab);

      assetGroups.forEach((group, groupIndex) => {
        assets.push({
          id: assetGroups.length > 1 ? `v1-${mapped.assetId}-${groupIndex}` : `v1-${mapped.assetId}`,
          assetId: mapped.assetId,
          versionNumber: 1,
          status: 'published',
          scope: 'Global',
          type: mapped.type,
          journeyTab: mapped.journeyTab,
          category: group,
          fileURL: mapped.fileURL,
          thumbnailURL: mapped.thumbnailURL,
          author: 'System',
          authorRole: 'Global Admin',
          isActive: true,
          name: mapped.name,
          productLine: mapped.productLine,
          dimensions: mapped.dimensions,
          dimensionLookupCode: mapped.dimensionLookupCode,
          description: mapped.description || (mapped.sectionMapping ? `Asset for ${mapped.sectionMapping}` : mapped.name),
          registryMapping: mapped.registryMapping,
          sectionMapping: mapped.sectionMapping,
          pilotId,
          statusHistory: [
            {
              status: 'published',
              changedBy: 'System',
              role: 'Global Admin',
              changedAt: new Date().toISOString(),
            },
          ],
        });
      });
    });
  };

  appendAssets(flattenBrandGuidelines(response.brand_guidelines), 'brand');

  JOURNEY_BUCKETS.forEach(({ key, tab }) => {
    appendAssets(flattenMediaBucket(response[key]), tab);
  });

  return assets;
};

const emptyResponse = (): GetMediaImagesResponse => ({
  brand_guidelines: {},
  email: [],
  dashboard: [],
  her: [],
  totalCount: 0,
  counts: {
    brand_guidelines: 0,
    email: 0,
    dashboard: 0,
    her: 0,
  },
});

const countBucketItems = (bucket: unknown): number => flattenMediaBucket(bucket).length;

const parseResponseBody = (body: unknown): GetMediaImagesResponse => {
  if (!body || typeof body !== 'object') return emptyResponse();

  const data = body as Partial<GetMediaImagesResponse>;
  const brandGuidelines = data.brand_guidelines ?? {};
  const email = data.email ?? [];
  const dashboard = data.dashboard ?? [];
  const her = data.her ?? [];

  return {
    brand_guidelines: brandGuidelines,
    email,
    dashboard,
    her,
    totalCount: typeof data.totalCount === 'number' ? data.totalCount : 0,
    counts: {
      brand_guidelines: data.counts?.brand_guidelines ?? countBucketItems(brandGuidelines),
      email: data.counts?.email ?? countBucketItems(email),
      dashboard: data.counts?.dashboard ?? countBucketItems(dashboard),
      her: data.counts?.her ?? countBucketItems(her),
    },
  };
};

const ACCEPTED_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico']);

export const isAcceptedMediaFile = (file: File): boolean => {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ACCEPTED_IMAGE_EXTENSIONS.has(ext)) return true;
  return file.type.startsWith('image/');
};

const CONFIG_NOT_FOUND_CODE = 'CONFIG_NOT_FOUND';

const extractResponseErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;

  const record = error as Record<string, unknown>;
  if (typeof record.code === 'string') return record.code;

  const details = record.details;
  if (details && typeof details === 'object') {
    const detailsRecord = details as Record<string, unknown>;
    if (typeof detailsRecord.code === 'string') return detailsRecord.code;
    if (typeof detailsRecord.error_code === 'string') return detailsRecord.error_code;
  }

  return undefined;
};

const isConfigNotFoundResponse = (body: unknown): boolean => {
  if (!body || typeof body !== 'object') return false;

  const record = body as Record<string, unknown>;
  const nestedError = record.error;
  const code =
    record.code ??
    record.error_code ??
    (nestedError && typeof nestedError === 'object'
      ? (nestedError as Record<string, unknown>).code
      : undefined);

  return code === CONFIG_NOT_FOUND_CODE;
};

const parseUnresolvedConfigValue = (body: unknown): string => {
  if (body == null) return '';
  if (typeof body === 'string') return normalizeImageUrl(body);

  if (typeof body !== 'object') return '';

  const record = body as Record<string, unknown>;
  const nested = record.data;
  const sources = [record, nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : null].filter(
    Boolean,
  ) as Record<string, unknown>[];

  for (const source of sources) {
    for (const key of [
      'configValue',
      'config_value',
      'value',
      'unresolvedValue',
      'unresolved_value',
      'defaultValue',
      'default_value',
    ]) {
      const url = normalizeImageUrl(source[key]);
      if (url) return url;
    }
  }

  return '';
};

const parseEnvMatrixEnvironment = (raw: unknown): MediaEnvMatrixEnvironment | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;

  const data = raw as Record<string, unknown>;
  const id = Number(data.id);
  if (!Number.isFinite(id)) return undefined;

  return {
    id,
    type: String(data.type ?? ''),
    environmentName: String(data.environmentName ?? data.environment_name ?? ''),
    dataServerUrl: String(data.dataServerUrl ?? data.data_server_url ?? ''),
  };
};

const envMatrixEnvironmentToAppEnv = (env: MediaEnvMatrixEnvironment): AppEnvironment | null =>
  projectEnvironmentToAppEnv({
    type: env.type,
    environment_name: env.environmentName,
    name: env.environmentName ?? '',
    stack_name: '',
    id: String(env.id),
  });

const parseEnvMatrixConfigEntry = (raw: unknown): MediaEnvMatrixConfigEntry | null => {
  if (!raw || typeof raw !== 'object') return null;

  const data = raw as Record<string, unknown>;
  const configKey = String(data.configKey ?? data.config_key ?? '').trim();
  const configType = String(data.configType ?? data.config_type ?? '').trim();
  if (!configKey || !configType) return null;

  const currentRaw = data.current;
  const currentEnv = parseEnvMatrixEnvironment(
    currentRaw && typeof currentRaw === 'object' ? (currentRaw as Record<string, unknown>).env : undefined,
  );
  if (!currentEnv) return null;

  const currentValueRaw =
    currentRaw && typeof currentRaw === 'object'
      ? (currentRaw as Record<string, unknown>).value
      : undefined;
  const currentValue =
    currentValueRaw == null ? undefined : String(currentValueRaw).trim() || undefined;

  const targetsRaw = Array.isArray(data.targets) ? data.targets : [];
  const targets = targetsRaw
    .map((target) => {
      if (!target || typeof target !== 'object') return null;
      const targetData = target as Record<string, unknown>;
      const env = parseEnvMatrixEnvironment(targetData.env);
      if (!env) return null;

      const valueRaw = targetData.value;
      const value = valueRaw == null ? undefined : String(valueRaw).trim() || undefined;

      return {
        env,
        value,
        isMatching: Boolean(targetData.isMatching ?? targetData.is_matching),
      };
    })
    .filter((target): target is NonNullable<typeof target> => target != null);

  return {
    configType,
    configKey,
    compositeKey: String(data.compositeKey ?? data.composite_key ?? `${configType}::${configKey}`),
    current: { env: currentEnv, value: currentValue },
    targets,
    allTargetsMatch: Boolean(data.allTargetsMatch ?? data.all_targets_match),
  };
};

const parseEnvMatrixResponse = (body: unknown): MediaEnvMatrixResponse | null => {
  if (!body || typeof body !== 'object') return null;

  const data = body as Record<string, unknown>;
  const currentEnv = parseEnvMatrixEnvironment(data.currentEnv ?? data.current_env);
  if (!currentEnv) return null;

  const targetEnvironmentsRaw = data.targetEnvironments ?? data.target_environments;
  const targetEnvironments = (Array.isArray(targetEnvironmentsRaw) ? targetEnvironmentsRaw : [])
    .map(parseEnvMatrixEnvironment)
    .filter((env): env is MediaEnvMatrixEnvironment => env != null);

  const configsRaw = Array.isArray(data.configs) ? data.configs : [];
  const configs = configsRaw
    .map(parseEnvMatrixConfigEntry)
    .filter((entry): entry is MediaEnvMatrixConfigEntry => entry != null);

  return {
    pilotId: String(data.pilotId ?? data.pilot_id ?? ''),
    currentEnv,
    targetEnvironments,
    configs,
    allConfigsMatch: Boolean(data.allConfigsMatch ?? data.all_configs_match),
  };
};

/** Map env-matrix config entries to per-asset env URLs for the compare grid. */
export const envMatrixConfigsToCompareUrls = (
  configs: MediaEnvMatrixConfigEntry[],
  currentAppEnv: AppEnvironment,
): Record<string, Partial<Record<AppEnvironment, string>>> => {
  const updates: Record<string, Partial<Record<AppEnvironment, string>>> = {};

  for (const config of configs) {
    const envUrls: Partial<Record<AppEnvironment, string>> = {};
    envUrls[currentAppEnv] = config.current.value ?? '';

    for (const target of config.targets) {
      const appEnv = envMatrixEnvironmentToAppEnv(target.env);
      if (!appEnv) continue;
      envUrls[appEnv] = target.value ?? '';
    }

    updates[config.configKey] = envUrls;
  }

  return updates;
};

export const MediaAPI = {
  getUnresolvedConfigValue: async (
    rc: RunContextApp,
    params: GetUnresolvedConfigParams,
  ): Promise<GetUnresolvedConfigResult> => {
    try {
      const response = await rc.bffClient.getUnresolvedConfigValue(params);

      if ('error' in response && response.error) {
        if (extractResponseErrorCode(response.error) === CONFIG_NOT_FOUND_CODE) {
          return { success: true, notFound: true };
        }

        const message =
          typeof response.error === 'string'
            ? response.error
            : (response.error as { message?: string })?.message || 'Failed to fetch config value';

        return { success: false, error: message };
      }

      if ('data' in response && response.data != null) {
        if (isConfigNotFoundResponse(response.data)) {
          return { success: true, notFound: true };
        }

        const value = parseUnresolvedConfigValue(response.data);
        return { success: true, value: value || undefined };
      }

      return { success: true };
    } catch (error) {
      rc.error(
        rc.getName(MediaAPI),
        `Error >> GET request to : /v1/media/unresolved-config/${params.pilot_id}\n\nresulted in error : \n`,
        error,
      );

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  getImages: async (rc: RunContextApp, params: GetMediaImagesParams): Promise<GetMediaImagesResult> => {
    try {
      const response = await rc.bffClient.getMediaImages(params);

      if ('error' in response && response.error) {
        const message =
          typeof response.error === 'string'
            ? response.error
            : (response.error as { message?: string })?.message || 'Failed to fetch media images';

        return { success: false, assets: [], error: message };
      }

      if ('data' in response && response.data != null) {
        const parsed = parseResponseBody(response.data);
        return {
          success: true,
          response: parsed,
          assets: transformMediaImagesResponseToAssets(parsed, params.project_id),
        };
      }

      const empty = emptyResponse();
      return { success: true, response: empty, assets: [] };
    } catch (error) {
      rc.error(
        rc.getName(MediaAPI),
        `Error >> GET request to : /v1/media/get-images/${params.project_id}\n\nresulted in error : \n`,
        error,
      );

      return {
        success: false,
        assets: [],
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  upload: async (rc: RunContextApp, params: UploadMediaParams): Promise<UploadMediaResult> => {
    try {
      const response = await rc.bffClient.uploadMedia(params);

      if ('error' in response && response.error) {
        const message =
          typeof response.error === 'string'
            ? response.error
            : (response.error as { message?: string })?.message || 'Failed to upload media';

        return { success: false, error: message };
      }

      const body = 'data' in response ? response.data : null;
      if (!body || typeof body !== 'object') {
        return { success: false, error: 'Invalid upload response from server' };
      }

      const data = body as Partial<{ url?: string; pushed_to_dataserver?: boolean }>;
      const url = typeof data.url === 'string' ? data.url.trim() : '';
      if (!url) {
        return { success: false, error: 'Upload succeeded but no image URL was returned' };
      }

      return {
        success: true,
        response: {
          url,
          pushed_to_dataserver: Boolean(data.pushed_to_dataserver),
        },
      };
    } catch (error) {
      rc.error(
        rc.getName(MediaAPI),
        'Error >> POST request to : /v1/media/upload\n\nresulted in error : \n',
        error,
      );

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  revertConfig: async (rc: RunContextApp, params: RevertMediaConfigParams): Promise<RevertMediaConfigResult> => {
    try {
      const response = await rc.bffClient.revertMediaConfig(params);

      if ('error' in response && response.error) {
        const message =
          typeof response.error === 'string'
            ? response.error
            : (response.error as { message?: string })?.message || 'Failed to revert media config';

        return { success: false, error: message };
      }

      const body = 'data' in response ? response.data : null;
      if (!body || typeof body !== 'object') {
        return { success: false, error: 'Invalid revert response from server' };
      }

      const data = body as Partial<RevertMediaConfigResponse>;
      const defaultValue = typeof data.defaultValue === 'string' ? data.defaultValue.trim() : '';
      if (!defaultValue) {
        return { success: false, error: 'Revert succeeded but no default image URL was returned' };
      }

      return {
        success: true,
        response: {
          configKey: String(data.configKey ?? params.configKey),
          configType: String(data.configType ?? params.configType),
          defaultValue,
          environmentId: typeof data.environmentId === 'number' ? data.environmentId : 0,
          projectId: String(data.projectId ?? params.projectId),
          pushed_to_dataserver: Boolean(data.pushed_to_dataserver),
        },
      };
    } catch (error) {
      rc.error(
        rc.getName(MediaAPI),
        'Error >> POST request to : /v1/media/revert-config\n\nresulted in error : \n',
        error,
      );

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  promoteConfig: async (rc: RunContextApp, params: PromoteMediaConfigParams): Promise<PromoteMediaConfigResult> => {
    try {
      const response = await rc.bffClient.promoteMediaConfig(params);

      if ('error' in response && response.error) {
        const message =
          typeof response.error === 'string'
            ? response.error
            : (response.error as { message?: string })?.message || 'Failed to promote media config';

        return { success: false, error: message };
      }

      const body = 'data' in response ? response.data : null;
      if (!body || typeof body !== 'object') {
        return { success: false, error: 'Invalid promote response from server' };
      }

      const data = body as Record<string, unknown>;
      const configValue = typeof data.configValue === 'string' ? data.configValue.trim() : '';
      const environment = data.environment;
      const parsedEnvironment =
        environment && typeof environment === 'object'
          ? {
              id: Number((environment as Record<string, unknown>).id),
              type: String((environment as Record<string, unknown>).type ?? ''),
              environmentName: String(
                (environment as Record<string, unknown>).environmentName ??
                  (environment as Record<string, unknown>).environment_name ??
                  '',
              ),
              dataServerUrl: String(
                (environment as Record<string, unknown>).dataServerUrl ??
                  (environment as Record<string, unknown>).data_server_url ??
                  '',
              ),
            }
          : undefined;

      return {
        success: true,
        response: {
          pilotId: String(data.pilotId ?? params.pilotId),
          environmentId: Number(data.environmentId ?? params.envId),
          environment: parsedEnvironment,
          configType: String(data.configType ?? params.configType),
          configKey: String(data.configKey ?? params.configKey),
          configValue: configValue || params.configValue,
          pushedToDataserver: Boolean(data.pushedToDataserver ?? data.pushed_to_dataserver),
        },
      };
    } catch (error) {
      rc.error(
        rc.getName(MediaAPI),
        'Error >> POST request to : /v1/media/promote-config\n\nresulted in error : \n',
        error,
      );

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  getEnvMatrix: async (rc: RunContextApp, params: GetMediaEnvMatrixParams): Promise<GetMediaEnvMatrixResult> => {
    try {
      const response = await rc.bffClient.getMediaEnvMatrix(params.pilotId, {
        current_env_id: params.currentEnvId,
        target_env_ids: params.targetEnvIds,
        target_configs: params.targetConfigs,
      });

      if ('error' in response && response.error) {
        const message =
          typeof response.error === 'string'
            ? response.error
            : (response.error as { message?: string })?.message || 'Failed to fetch environment matrix';

        return { success: false, error: message };
      }

      const body = 'data' in response ? response.data : null;
      const parsed = parseEnvMatrixResponse(body);
      if (!parsed) {
        return { success: false, error: 'Invalid environment matrix response from server' };
      }

      return { success: true, response: parsed };
    } catch (error) {
      rc.error(
        rc.getName(MediaAPI),
        `Error >> POST request to : /v1/media/env-matrix/${params.pilotId}\n\nresulted in error : \n`,
        error,
      );

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },
};
